import axios from "axios";
import { api } from "./typed";
import type { components } from "./generated/backend";

/**
 * Skill search, similar-skill lookup and suggestions (backend#992, #1061).
 *
 * Search runs on the server over names *and* admin-managed aliases and comes
 * back ranked, with `matched_alias` saying which alias found a skill. Callers
 * must not re-filter the results by name — that would discard alias matches.
 */

export type SkillSearchResult = components["schemas"]["SkillSearchResult"];
export type PublicSkillSearchResult = components["schemas"]["PublicSkillResult"];
export type SkillSuggestion = components["schemas"]["SkillSuggestionResponse"];
export type SkillSuggestionOutcome = components["schemas"]["SkillSuggestionOutcome"];

/** How many ranked results a picker asks for. */
export const SKILL_SEARCH_LIMIT = 20;
/** How many skills the admin "similar skills exist" warning lists. */
const SIMILAR_LIMIT = 5;

/** The skill a 409 duplicate response points at (`detail.existing_skill`). */
export interface ExistingSkillClash {
  skill: { id: number; name: string };
  matchedAlias: string | null;
}

/** Reads the structured 409 body of a duplicate skill name or alias. */
export const existingSkillFromConflict = (err: unknown): ExistingSkillClash | null => {
  if (!axios.isAxiosError(err) || err.response?.status !== 409) {
    return null;
  }
  const detail = (err.response.data as { detail?: unknown } | undefined)?.detail;
  if (typeof detail !== "object" || detail === null) {
    return null;
  }
  const { existing_skill: skill, matched_alias: matchedAlias } = detail as {
    existing_skill?: { id?: unknown; name?: unknown };
    matched_alias?: unknown;
  };
  if (!skill || typeof skill.id !== "number" || typeof skill.name !== "string") {
    return null;
  }
  return {
    skill: { id: skill.id, name: skill.name },
    matchedAlias: typeof matchedAlias === "string" ? matchedAlias : null,
  };
};

export const skillsAPI = {
  /** `GET /skills/?search=` — ranked matches on name or alias. */
  search: (query: string): Promise<SkillSearchResult[]> =>
    api.get("/skills/", { query: { search: query, limit: SKILL_SEARCH_LIMIT } }),

  /** `GET /skills/similar` — what an admin sees before creating a skill. */
  findSimilar: (name: string): Promise<SkillSearchResult[]> =>
    api.get("/skills/similar", { query: { name, limit: SIMILAR_LIMIT } }),

  /**
   * `POST /skills/` (admins). The toast is suppressed so the caller can turn
   * a 409 duplicate into "use the existing skill" instead of an error.
   */
  create: async (name: string): Promise<{ id: number; name: string }> =>
    (await api.post("/skills/", {
      body: { name },
      config: { suppressErrorToast: true },
    })) as { id: number; name: string },

  /** `POST /skills/suggestions` — a consultant suggests a missing skill. */
  suggest: (text: string): Promise<SkillSuggestionOutcome> =>
    api.post("/skills/suggestions", { body: { text } }),

  /** `GET /skills/suggestions/mine` — my suggestions awaiting review. */
  myPendingSuggestions: (): Promise<SkillSuggestion[]> =>
    api.get("/skills/suggestions/mine", {
      query: { status: "pending" },
      config: { suppressErrorToast: true },
    }),
};
